import store from '../state';

// Get the API URL from environment variables and log it for debugging
const API_URL = process.env.REACT_APP_USER_API_URL || 'http://localhost:5002';
console.log('User Service API URL:', API_URL);

/**
 * Service for user-related API operations
 */
const userService = {
  /**
   * Fetch a single user by ID
   * @param {string} userId - The ID of the user to fetch
   * @returns {Promise<Object>} - The user data
   */
  async fetchUser(userId) {
    try {
      // Handle case where userId is an object instead of a string
      let userIdToUse;
      
      if (userId === null || userId === undefined) {
        console.error("fetchUser called with null/undefined userId");
        return {
          id: "unknown",
          username: "Unknown User",
          name: "Unknown User"
        };
      }
      
      if (typeof userId === 'object') {
        console.log("fetchUser received an object instead of a string ID:", userId);
        // Try to extract a usable ID - prioritize 'userId' over 'id' since that's the field in sharedWith
        if (userId.userId) {
          userIdToUse = userId.userId;
        } else if (userId.id) {
          userIdToUse = userId.id;
        } else {
          // Return a placeholder user if no ID can be extracted
          return {
            id: "object",
            username: "User",
            name: "User"
          };
        }
        console.log("Extracted ID from object:", userIdToUse);
      } else {
        userIdToUse = userId;
      }
      
      // Ensure userId is a string for safety
      const userIdStr = String(userIdToUse || '');
      const encodedId = encodeURIComponent(userIdStr);
      console.log(`Fetching user with ID ${userIdStr} from ${API_URL}/users?userId=${encodedId}`);
      
      // Get token from Redux store
      const state = store.getState();
      const token = state.auth?.token;
      
      // Include Authorization token in headers
      const response = await fetch(`${API_URL}/users?userId=${encodedId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Api-Name': 'user-api',
          'Authorization': token && token !== 'null' ? `Bearer ${token}` : undefined
        }
      });
      
      if (!response.ok) {
        console.error(`Error fetching user: ${response.status}`);
        return {
          id: userIdStr,
          username: `User (${userIdStr.substring(0, 8)})`,
          name: `User (${userIdStr.substring(0, 8)})`,
        };
      }
      
      const data = await response.json();
      console.log('User data received:', data);
      
      // Handle different response formats
      if (data.user && typeof data.user === 'object') {
        return data.user;
      } 
      else if (data && (data.id || data.userId)) {
        return data;
      }
      else if (data.users && Array.isArray(data.users) && data.users.length > 0) {
        return data.users[0];
      }
      
      // Return a default object if we couldn't parse the response
      return {
        id: userIdStr,
        username: `User (${userIdStr.substring(0, 8)})`,
        name: `User (${userIdStr.substring(0, 8)})`,
      };
    } catch (error) {
      console.error(`Error fetching user with ID ${userId}:`, error);
      
      // Ensure userId is a string for safety
      const userIdStr = String(userId && typeof userId === 'object' ? (userId.userId || userId.id || 'unknown') : (userId || ''));
      
      // Return a default user object even on error
      return {
        id: userIdStr,
        username: `User (${userIdStr.substring(0, 8)})`,
        name: `User (${userIdStr.substring(0, 8)})`,
      };
    }
  },

  /**
   * Fetch multiple users by their IDs
   * @param {Array<string>} userIds - Array of user IDs to fetch
   * @returns {Promise<Array<Object>>} - Array of user data objects
   */
  async fetchUsersBatch(userIds) {
    try {
      if (!userIds || !userIds.length) {
        return [];
      }
      
      // Remove any duplicate IDs and ensure all IDs are strings
      const uniqueIds = [...new Set(userIds)].map(id => String(id || ''));
      
      // Properly encode each ID separately before joining
      const encodedIds = uniqueIds.map(id => encodeURIComponent(id)).join(',');
      
      // Use the batch endpoint with comma-separated IDs in query params
      const url = `${API_URL}/batch?userIds=${encodedIds}`;
      console.log(`Fetching batch users at: ${url}`);
      
      // Get token from Redux store
      const state = store.getState();
      const token = state.auth?.token;
      
      // Include Authorization token in headers
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Api-Name': 'user-api',
          'Authorization': token && token !== 'null' ? `Bearer ${token}` : undefined
        }
      });
      
      if (!response.ok) {
        console.error(`Batch request failed with status: ${response.status}`);
        
        // Fall back to individual requests if batch fails
        console.log('Falling back to individual requests');
        const users = await Promise.all(
          uniqueIds.map(async (id) => {
            try {
              console.log(`Fetching individual user: ${id}`);
              const userData = await this.fetchUser(id);
              return userData;
            } catch (e) {
              console.error(`Failed to fetch individual user ${id}:`, e);
              return null;
            }
          })
        );
        return users.filter(Boolean);
      }
      
      const data = await response.json();
      console.log('Batch users response:', data);
      
      // Handle different response formats
      if (data && data.users && Array.isArray(data.users)) {
        return data.users;
      } else if (Array.isArray(data)) {
        return data;
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching batch users:', error);
      return [];
    }
  },

  /**
   * Search for users by name or username
   * @param {string} query - The search query
   * @param {string} userId - The current user's ID
   * @param {string} connectionType - Optional connection type filter
   * @returns {Promise<Array<Object>>} - Array of matching users
   */
  async searchUsers(query, userId = null, connectionType = null) {
    try {
      const encodedQuery = encodeURIComponent(query);
      let url = `${API_URL}/search?q=${encodedQuery}`;
      
      if (userId) {
        url += `&userId=${encodeURIComponent(userId)}`;
      }
      
      if (connectionType) {
        url += `&connectionType=${encodeURIComponent(connectionType)}`;
      }
      
      console.log(`Searching users with query "${query}" at ${url}`);
      
      // Get token from Redux store
      const state = store.getState();
      const token = state.auth?.token;
      
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Api-Name': 'user-api',
          'Authorization': token && token !== 'null' ? `Bearer ${token}` : undefined
        }
      });
      
      if (!response.ok) {
        throw new Error(`Error searching users: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Search results:', data);
      
      // Handle different response formats
      if (data && data.users && Array.isArray(data.users)) {
        return data.users;
      } else if (Array.isArray(data)) {
        return data;
      }
      return [];
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  }
};

// Add export statement
export default userService; 